import React from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { Loader2, X } from "lucide-react";

// ===========================================================================
// WorkspaceShell — the shared full-screen workspace container used by both the
// Prospect Workspace and the Candidate Workspace. It guarantees an identical
// modal structure, sizing, close affordance, scrollable body and sticky footer
// so keyboard accessibility & theming stay consistent across modules.
//
// Keyboard a11y contract (must be preserved by all consumers):
//  - The mouse-only close "X" is intentionally kept OUT of the tab order
//    (tabIndex=-1) so the footer's primary action remains the final tab stop.
//  - Esc closes the dialog (handled by Radix).
//  - `hideDefaultClose` removes Radix's built-in close button.
// ===========================================================================
export const WorkspaceShell = ({
  open = true,
  onClose,
  loading = false,
  ariaLabel,
  testid,
  closeTestid,
  header,
  footer,
  children,
}) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
    <DialogContent
      className="bg-[#0E1424] border-white/10 text-white p-0 gap-0 w-[80vw] max-w-[80vw] h-[86vh] overflow-hidden flex flex-col"
      data-testid={testid}
      hideDefaultClose
      aria-describedby={undefined}
    >
      <DialogTitle className="sr-only">{ariaLabel || "Workspace"}</DialogTitle>
      {loading ? (
        <div className="h-full flex items-center justify-center">
          <Loader2 className="animate-spin text-white/50" />
        </div>
      ) : (
        <>
          {/* Manual close (mouse only) — kept out of the tab order so the
              footer primary action remains the final tab stop. Esc also closes. */}
          <button
            type="button"
            tabIndex={-1}
            aria-hidden="true"
            onClick={onClose}
            className="absolute right-4 top-4 z-10 rounded-md p-1 text-white/50 hover:text-white hover:bg-white/10 transition-colors"
            data-testid={closeTestid || "workspace-close-x"}
          >
            <X size={18} />
          </button>

          {/* Header */}
          <div className="px-6 py-4 border-b border-white/10 shrink-0">{header}</div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto">{children}</div>

          {/* Sticky footer */}
          {footer && (
            <div className="px-6 py-3 border-t border-white/10 shrink-0 flex items-center justify-end gap-2 bg-[#0E1424]">
              {footer}
            </div>
          )}
        </>
      )}
    </DialogContent>
  </Dialog>
);

// Consistent workspace header: title + badges + subtitle on the left, quick
// actions on the right.
export const WorkspaceHeader = ({ title, badges, subtitle, actions }) => (
  <div className="flex items-start justify-between gap-4 flex-wrap">
    <div className="min-w-0">
      <div className="flex items-center gap-3 flex-wrap">
        <h2 className="text-xl font-semibold text-white truncate">{title}</h2>
        {badges}
      </div>
      {subtitle && <div className="text-white/50 text-sm mt-0.5">{subtitle}</div>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

const baseLinkCls =
  "inline-flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-sm transition-colors";

// Quick action pill: renders a real link when a value exists, otherwise a
// disabled control with an explanatory tooltip. `onClick` may be provided for
// non-href actions (e.g. resume download).
export const WorkspaceQuickLink = ({ href, onClick, icon: Icon, label, testid, disabledTip, external }) => {
  if (href) {
    return (
      <a
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noreferrer" : undefined}
        className={`${baseLinkCls} text-white/80 hover:bg-white/5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring`}
        data-testid={testid}
      >
        <Icon size={14} /> {label}
      </a>
    );
  }
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${baseLinkCls} text-white/80 hover:bg-white/5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring`}
        data-testid={testid}
      >
        <Icon size={14} /> {label}
      </button>
    );
  }
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            disabled
            aria-disabled="true"
            className={`${baseLinkCls} text-white/40 opacity-60 cursor-not-allowed`}
            data-testid={testid}
          >
            <Icon size={14} /> {label}
          </button>
        </TooltipTrigger>
        <TooltipContent className="bg-[#0E1424] border-white/10 text-white text-xs">
          {disabledTip || `No ${label.toLowerCase()} available.`}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Label/value row used in the right-column info cards.
export const WorkspaceDetailField = ({ label, value, icon: Icon }) => (
  <div className="flex items-start gap-2 text-sm">
    {Icon && <Icon size={14} className="text-white/35 mt-0.5 shrink-0" />}
    <span className="text-white/45 w-28 shrink-0">{label}</span>
    <span className="text-white/85 break-words">{value || "\u2014"}</span>
  </div>
);

// Ensure external links carry a protocol so the browser doesn't treat them as
// relative URLs.
export const normalizeUrl = (u) => {
  if (!u) return null;
  const s = String(u).trim();
  if (!s) return null;
  return /^https?:\/\//i.test(s) ? s : `https://${s}`;
};
